import React from 'react';
import { Link } from 'react-router-dom';

const METRICS = [
  { value: '4', label: 'Single-author preprints, 23 July to 19 August 2026' },
  { value: '3', label: 'Open code and data deposits on Zenodo' },
  { value: '18', label: 'Public methylation cohorts reanalyzed' },
  { value: '2,300+', label: 'Public arrays re-decoded from raw files' },
];

const PRINCIPLES = [
  {
    title: 'Public data first',
    text: 'Our published analyses use only publicly deposited cohorts, so any reader can obtain the same inputs. Controlled-access and patient data are never used in public-facing results.',
  },
  {
    title: 'Raw files, one pipeline',
    text: 'Where raw array files exist, we decode them ourselves with a single locked normalization, so healthy references and disease samples are processed identically and inputs are checksum-verified.',
  },
  {
    title: 'Plans frozen before results',
    text: 'Statistical analysis plans, endpoints and pass/fail boundaries are written and frozen before any score is computed. Every later amendment is logged with whether results had been seen.',
  },
  {
    title: 'Limitations first',
    text: 'Each paper states what the data cannot show before what they can: confounds, sample sizes, non-significant results labeled as such, and claims that remain goals.',
  },
  {
    title: 'Code and data you can rerun',
    text: 'Analysis code and derived tables are deposited on Zenodo under open licenses (CC BY 4.0 for data, MIT for code), linked from each preprint.',
  },
];

const PUBLICATIONS = [
  {
    date: '19 Aug 2026',
    type: 'Preprint',
    title: 'Leukocyte Composition Change Accounts for Most Apparent Ten-Year DNA Methylation Drift in Blood: A Two-Cohort Longitudinal Analysis',
    doi: '10.21203/rs.3.rs-10733012/v1',
  },
  {
    date: 'Aug 2026',
    type: 'Code & data',
    title: 'Analysis code and derived tables for the composition-drift study (all versions)',
    doi: '10.5281/zenodo.21986629',
  },
  {
    date: '29 Jul 2026',
    type: 'Preprint',
    title: 'Epigenetic Age Estimates in Blood Shift by Decades with Cell Population: A Within-Donor Variance Partition Across Two Platforms and Two Study Designs',
    doi: '10.21203/rs.3.rs-10503047/v1',
  },
  {
    date: 'Jul 2026',
    type: 'Code & data',
    title: 'Analysis code and derived results for the epigenetic-age study (all versions)',
    doi: '10.5281/zenodo.21538619',
  },
  {
    date: '27 Jul 2026',
    type: 'Preprint',
    title: 'Compartment Mismatch Produces Near-Perfect Apparent Discrimination in Blood-Referenced DNA-Methylation Classifiers: A Public-Cohort Reanalysis of Hematologic-Malignancy Series',
    doi: '10.21203/rs.3.rs-10472780/v1',
  },
  {
    date: 'Jul 2026',
    type: 'Code & data',
    title: 'Derived results for the compartment-mismatch study',
    doi: '10.5281/zenodo.21517032',
  },
  {
    date: '23 Jul 2026',
    type: 'Preprint',
    title: 'Static DNA-Methylation Burden Discriminates Hematologic-Malignancy Subtypes but Does Not License Per-Patient Trajectory Inference: A Public-Cohort Analysis and an Identifiability Framework',
    doi: '10.21203/rs.3.rs-10397076/v1',
  },
];

const FINDINGS = [
  {
    label: 'Finding',
    title: 'Tissue mismatch manufactures accuracy',
    finding: 'In published blood-cancer datasets, comparing patient samples from one tissue with healthy controls from another produced near-perfect apparent discrimination. When cases and controls came from the same tissue, most of that separation disappeared. Standard epigenetic clocks also shifted by decades depending on which blood cells were measured.',
    decision: 'Alercell compares cases and controls only within matched specimen types, and reports specimen preparation as a primary variable.',
  },
  {
    label: 'Finding',
    title: 'Most ten-year drift in blood is composition',
    finding: 'Following the same older adults over ten years, about two thirds of the apparent change in a blood methylation score was explained by the normal age-related shift in white-cell composition. The remainder was not statistically distinguishable from zero at that sample size.',
    decision: 'Specimen type is locked per person across repeat draws, and cell composition is measured and adjusted for as a primary analysis, not assumed away.',
  },
];

const COHORTS = [
  { group: 'Cell-type references', rows: [
    ['GSE35069', 'Purified blood cell populations from healthy donors', '450K'],
    ['GSE110554', 'Purified leukocyte populations', 'EPIC'],
  ] },
  { group: 'Healthy blood', rows: [
    ['GSE87571', 'Healthy whole blood across the adult age range', '450K'],
    ['GSE40279', 'Healthy whole blood', '450K'],
    ['GSE42861', 'Peripheral blood leukocytes, control arm', '450K'],
    ['GSE51057', 'Buffy coat', '450K'],
    ['GSE55763', 'Whole blood, population cohort', '450K'],
    ['GSE69270', 'Whole blood, population cohort', '450K'],
  ] },
  { group: 'Longitudinal, repeat draws', rows: [
    ['GSE73115', 'Danish elderly twins, draws ten years apart', '450K'],
    ['E-MTAB-7309', 'Swedish adult twins, up to five draws per person', '450K'],
    ['GSE130748', 'Older US adults, draws five years apart', 'EPIC'],
  ] },
  { group: 'Hematologic malignancy', rows: [
    ['GSE58477', 'Acute myeloid leukemia and normal marrow progenitors', '450K'],
    ['GSE315367', 'Acute myeloid leukemia, serial samples', 'EPIC'],
    ['GSE105420', 'Chronic myelomonocytic leukemia', '450K'],
    ['GSE106600', 'Chronic myeloid leukemia, sorted cells', '450K'],
    ['GSE118241', 'Myelofibrosis', '450K'],
    ['E-MTAB-7575', 'Chronic lymphocytic leukemia', '450K'],
    ['GSE230487', 'Blastic plasmacytoid dendritic cell neoplasm', 'EPIC'],
  ] },
];

const accessionUrl = acc => acc.startsWith('E-MTAB')
  ? `https://www.ebi.ac.uk/biostudies/arrayexpress/studies/${acc}`
  : `https://www.ncbi.nlm.nih.gov/geo/query/acc.cgi?acc=${acc}`;

const linkStyle = { color: 'var(--accent-cyan)', textDecoration: 'none' };
const mono = { fontFamily: 'var(--font-mono)', fontSize: 12 };

export default function Research() {
  return (
    <main>
      {/* Hero */}
      <section className="page-hero">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <span className="eyebrow">Research & Reproducibility</span>
          <h1 className="hero-headline" style={{ marginBottom: 24, maxWidth: 900 }}>Open methods. Public data. Results anyone can rerun.</h1>
          <p className="body-lg" style={{ maxWidth: 720 }}>
            Alercell publishes its methodological research as preprints with open code and data. We test our own assumptions on independent public cohorts and report what the data do not support alongside what they do.
          </p>
          <div style={{ display: 'flex', gap: 20, marginTop: 32, flexWrap: 'wrap' }}>
            {[['Principles', 'principles'], ['Publications', 'publications'], ['What we learned', 'findings'], ['Public cohorts', 'cohorts'], ['Scope', 'scope']].map(([label, id]) => (
              <a key={id} href={`#${id}`}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-cyan)', textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', padding: '6px 14px', border: '1px solid var(--accent-cyan-border)', borderRadius: 20 }}>
                {label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics */}
      <div className="metric-strip">
        <div className="container">
          <div className="metric-grid">
            {METRICS.map((m, i) => (
              <div key={i} className="metric-item">
                <span className="metric-value">{m.value}</span>
                <p className="metric-label">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Principles */}
      <section id="principles" className="section">
        <div className="container">
          <span className="eyebrow">How we work</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>Five rules we hold our own research to.</h2>
          <div className="cyan-line" />
          <div className="research-grid">
            {PRINCIPLES.map((p, i) => (
              <div key={i} className="bio-block" style={{ marginBottom: 0 }}>
                <div className="bio-block-title">{p.title}</div>
                <div className="bio-block-text">{p.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Publications */}
      <section id="publications" className="section section-surface">
        <div className="container">
          <span className="eyebrow">Publications & deposits</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>Preprints and the code behind them.</h2>
          <div className="cyan-line" />
          <p className="body-md section-intro">
            Preprints are posted on Research Square and have not yet been peer reviewed. Each code and data deposit link resolves to the latest version.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 640 }}>
              <thead>
                <tr><th>Date</th><th>Type</th><th>Title</th><th>DOI</th></tr>
              </thead>
              <tbody>
                {PUBLICATIONS.map((p, i) => (
                  <tr key={i}>
                    <td style={{ ...mono, whiteSpace: 'nowrap' }}>{p.date}</td>
                    <td style={{ whiteSpace: 'nowrap' }}><span className={p.type === 'Preprint' ? 'badge badge-active' : 'badge badge-complete'}>{p.type}</span></td>
                    <td style={{ color: 'var(--text-primary)' }}>{p.title}</td>
                    <td style={{ ...mono, wordBreak: 'break-all' }}>
                      <a href={`https://doi.org/${p.doi}`} target="_blank" rel="noopener noreferrer" style={linkStyle}>{p.doi}</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Findings */}
      <section id="findings" className="section">
        <div className="container">
          <span className="eyebrow">What we learned</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>Two findings that changed how we design our test.</h2>
          <div className="cyan-line" />
          <p className="body-md section-intro" style={{ marginBottom: 40 }}>
            Both results narrow what a blood methylation test can claim. We publish them because a diagnostic built without them would measure the sample tube, not the disease.
          </p>
          <div className="research-grid-2">
            {FINDINGS.map((f, i) => (
              <div key={i} className="card">
                <div className="card-label">{f.label}</div>
                <h3 className="card-title" style={{ margin: '10px 0 14px' }}>{f.title}</h3>
                <p className="body-md" style={{ marginBottom: 20 }}>{f.finding}</p>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                  <div className="card-label" style={{ marginBottom: 8 }}>Design decision</div>
                  <p className="body-md" style={{ color: 'var(--text-primary)' }}>{f.decision}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cohorts */}
      <section id="cohorts" className="section section-surface">
        <div className="container">
          <span className="eyebrow">Public cohorts</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>The independent data we test against.</h2>
          <div className="cyan-line" />
          <p className="body-md section-intro">
            All cohorts are publicly deposited in the Gene Expression Omnibus (GSE) or ArrayExpress (E-MTAB) and were generated by independent investigators, whom we thank for sharing their data. Platform refers to the Illumina Infinium array: HumanMethylation450 (450K) or MethylationEPIC (EPIC).
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 560 }}>
              <thead>
                <tr><th>Accession</th><th>Specimens</th><th>Platform</th></tr>
              </thead>
              {COHORTS.map(g => (
                <tbody key={g.group}>
                  <tr>
                    <td colSpan={3} style={{ ...mono, fontSize: 11, color: 'var(--accent-cyan)', textTransform: 'uppercase', letterSpacing: '0.1em', paddingTop: 24 }}>{g.group}</td>
                  </tr>
                  {g.rows.map(([acc, desc, platform]) => (
                    <tr key={acc}>
                      <td style={{ ...mono, whiteSpace: 'nowrap' }}>
                        <a href={accessionUrl(acc)} target="_blank" rel="noopener noreferrer" style={linkStyle}>{acc}</a>
                      </td>
                      <td>{desc}</td>
                      <td style={mono}>{platform}</td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        </div>
      </section>

      {/* Scope */}
      <section id="scope" className="section">
        <div className="container">
          <div className="research-grid-2">
            <div>
              <span className="eyebrow">Tools</span>
              <h2 className="section-headline" style={{ fontSize: 28, marginBottom: 8 }}>Methods and software</h2>
              <div className="cyan-line" />
              <p className="body-md" style={{ marginBottom: 16 }}>
                Analyses run in R and Python using standard Bioconductor packages for array decoding, normalization and cell-type deconvolution. Research questions, study designs, analyses and interpretation are Alercell's own.
              </p>
              <p className="body-md">
                AI tools, including Claude (Anthropic), are used to speed up coding and first drafts. The author runs every analysis and verifies every number and citation, as disclosed in the methods of our <a href="https://doi.org/10.21203/rs.3.rs-10733012/v1" target="_blank" rel="noopener noreferrer" style={linkStyle}>longitudinal study</a>.
              </p>
            </div>
            <div>
              <span className="eyebrow">Scope</span>
              <h2 className="section-headline" style={{ fontSize: 28, marginBottom: 8 }}>What this work does and does not show</h2>
              <div className="cyan-line" />
              <p className="body-md" style={{ marginBottom: 16 }}>
                The studies on this page are methodological reanalyses of public data. They are not clinical validation of a diagnostic test, and they report no patient data.
              </p>
              <p className="body-md">
                The LENA platform is for research use only and is not cleared or approved by the FDA. Detecting blood-cancer methylation change before symptoms appear is Alercell's goal, not a current claim. Establishing it requires a prospective study with repeat blood draws and recorded outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-surface">
        <div className="container cta-banner">
          <h2 className="section-headline">Questions about our data or code?</h2>
          <p className="body-lg">We welcome reanalysis, replication attempts and critique. Write to us at info@alercell.com.</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">Contact us</Link>
            <Link to="/news" className="btn btn-outline">All news</Link>
          </div>
        </div>
      </section>

      <style>{`
        .research-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 24px; }
        .research-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; }
        #principles, #publications, #findings, #cohorts, #scope { scroll-margin-top: 80px; }
        @media (max-width: 768px) { .research-grid-2 { grid-template-columns: 1fr; } .research-grid { grid-template-columns: 1fr; } }
      `}</style>
    </main>
  );
}
